import { datadogRum } from '@datadog/browser-rum';
import { MetaProvider, Title } from '@solidjs/meta';
import { ConfigManager } from '@utils/ConfigManager';
import { logError } from '@utils/errorLogging';
import { render } from 'solid-js/web';

import './components/Grid/AgGridStyles.css';
import './index.css';

const IS_MOBILE = import.meta.env.VITE_IS_MOBILE === 'true';

const getSanitizedReferrer = (referrer: string) => {
  if (referrer.trim().length === 0) {
    return 'direct';
  }

  try {
    const url = new URL(referrer);
    return `${url.origin}${url.pathname}`;
  } catch {
    return 'direct';
  }
};

const ORIGIN_CONTEXT = {
  referrer: getSanitizedReferrer(document.referrer),
};

datadogRum.init({
  applicationId: '1af8e89d-cf79-489d-8788-773e0a946941',
  clientToken: 'pubd8297b4db4ef7b0e588b1bf9b0f72e27',
  site: 'datadoghq.com',
  service: IS_MOBILE ? 'vnext-ui-mobile' : 'vnext-ui-desktop',
  env: ConfigManager.selectedEnv,
  version: '4.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'allow',
  trackFrustrations: true,
  allowedTracingUrls: [(url) => url.startsWith(ConfigManager.apiUrl)],
  beforeSend: (event) => {
    event.context = {
      ...event.context,
      origin: ORIGIN_CONTEXT,
    };

    if (event.type === 'view') {
      event.view.referrer =
        ORIGIN_CONTEXT.referrer === 'direct' ? '' : ORIGIN_CONTEXT.referrer;
    }

    return true;
  },
});

datadogRum.startSessionReplayRecording();

void (async () => {
  try {
    const root = document.getElementById('root');

    if (import.meta.env.DEV && !(root instanceof HTMLElement)) {
      throw new Error(
        'Root element not found. Did you forget to add it to your index.html? Or maybe the id attribute got misspelled?',
      );
    }

    const { default: RootApp } = IS_MOBILE
      ? await import('./MobileApp')
      : await import('./App');

    render(
      () => (
        <MetaProvider>
          <Title>AtgFr8</Title>
          <RootApp />
        </MetaProvider>
      ),
      root!,
    );
  } catch (error) {
    logError('unknown', error as Error);
    throw error;
  }
})();

window.onerror = (msg, _, lineNo, columnNo, error) => {
  if (error === undefined) {
    error = new Error(msg instanceof Event ? JSON.stringify(msg) : msg);
    error.stack = `${lineNo}:${columnNo}`;
  }
  // eslint-disable-next-line no-console
  console.log(error);

  logError('uncaughtError', error);
};

window.onunhandledrejection = function (error) {
  // eslint-disable-next-line no-console
  console.log('There was an unhandled error: ', error);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (Boolean(error.reason) && Boolean(error.reason.message)) {
    if (
      Boolean(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call
        error.reason.message.includes(
          'Failed to fetch dynamically imported module:',
        ),
      )
    ) {
      // eslint-disable-next-line no-console
      console.log(
        'There was an issue loading a new version of the app, reloading the page.',
      );
      window.location.reload();
    }
  }
};
